/**
 * CLEARO "clean the window" interaction
 *
 * A dirty-glass canvas covers the tiles in sections/clearo-every-home.liquid.
 * A CLEARO follows the pointer; holding and dragging wipes the dirt away
 * (touch: horizontal swipes, so vertical page scrolling still works).
 * "Clean it for me" runs an automatic zig-zag. Once enough glass is clean the
 * rest fades out and the section gets .is-inview (tiles play their mini-wipes).
 *
 * Without JS the overlay stays hidden and the tiles show normally.
 */
(() => {
  if (window.clearoWipe) return;
  window.clearoWipe = true;

  const coarse = window.matchMedia('(pointer: coarse)');
  const doneAt = () => (coarse.matches ? 0.4 : 0.6); // share of glass that must be clean (touch screens: less)
  const brushScale = () => (coarse.matches ? 1.3 : 0.9); // brush height relative to the cleaner's height
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Small seeded PRNG so the dirt looks the same on every visit
  const rng = (seed) => () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  class DirtyWindow {
    constructor(section) {
      this.section = section;
      this.el = section.querySelector('[data-clr-wipe]');
      this.canvas = this.el.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.cleaner = this.el.querySelector('.clr-wipe__cleaner');
      this.autoBtn = this.el.querySelector('[data-clr-wipe-auto]');
      this.pressing = false;
      this.last = null;
      this.done = false;
      this.lastCheck = 0;

      section.classList.add('is-armed');
      this.el.hidden = false;
      this.el.classList.add('is-idle');

      this.resize(true);
      this.moveCleaner(this.w / 2, this.h / 2);

      this.el.addEventListener('pointerdown', (e) => this.onDown(e));
      this.el.addEventListener('pointermove', (e) => this.onMove(e));
      window.addEventListener('pointerup', () => this.onUp());
      this.el.addEventListener('pointercancel', () => this.onUp());
      this.el.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && !this.pressing && this.park());
      this.autoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.autoClean();
      });
      this.autoBtn.addEventListener('pointerdown', (e) => e.stopPropagation());

      this.ro = new ResizeObserver(() => this.resize(false));
      this.ro.observe(this.el);
    }

    // ---------- drawing ----------
    resize(first) {
      const rect = this.el.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      if (!first && w === this.w && h === this.h) return;

      // keep progress when the layout changes
      let snapshot = null;
      if (!first && this.canvas.width) {
        snapshot = document.createElement('canvas');
        snapshot.width = this.canvas.width;
        snapshot.height = this.canvas.height;
        snapshot.getContext('2d').drawImage(this.canvas, 0, 0);
      }

      this.w = w;
      this.h = h;
      this.dpr = dpr;
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (snapshot) {
        this.ctx.globalCompositeOperation = 'source-over';
        this.ctx.drawImage(snapshot, 0, 0, w, h);
      } else {
        this.paintDirt();
      }
      this.brush = this.cleaner.offsetHeight * brushScale() || 100;
    }

    paintDirt() {
      const { ctx, w, h } = this;
      const r = rng(20260928);
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, w, h);

      // frosted grey-blue haze
      const base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, 'rgba(216, 223, 232, 0.95)');
      base.addColorStop(1, 'rgba(193, 202, 214, 0.95)');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      // dusty smudges
      for (let i = 0; i < Math.round((w * h) / 9000); i++) {
        const x = r() * w;
        const y = r() * h;
        const rad = 40 + r() * 150;
        const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
        const warm = r() > 0.6;
        g.addColorStop(0, warm ? 'rgba(168, 156, 132, 0.22)' : 'rgba(140, 150, 166, 0.26)');
        g.addColorStop(1, 'rgba(140, 150, 166, 0)');
        ctx.fillStyle = g;
        ctx.fillRect(x - rad, y - rad, rad * 2, rad * 2);
      }

      // specks of dirt
      for (let i = 0; i < Math.round((w * h) / 260); i++) {
        ctx.fillStyle = `rgba(110, 118, 132, ${0.15 + r() * 0.35})`;
        ctx.beginPath();
        ctx.arc(r() * w, r() * h, 0.4 + r() * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }

      // water streaks with a droplet at the bottom
      ctx.lineCap = 'round';
      for (let i = 0; i < Math.round(w / 14); i++) {
        const x = r() * w;
        const y = r() * h * 0.85;
        const len = 30 + r() * 150;
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.25 + r() * 0.3})`;
        ctx.lineWidth = 0.8 + r() * 1.6;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.bezierCurveTo(x + (r() - 0.5) * 6, y + len * 0.4, x + (r() - 0.5) * 8, y + len * 0.7, x + (r() - 0.5) * 4, y + len);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.beginPath();
        ctx.arc(x, y + len, ctx.lineWidth * 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    wipe(x0, y0, x1, y1) {
      const { ctx } = this;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = this.brush;
      ctx.shadowColor = 'rgba(0,0,0,1)';
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1 + 0.01, y1);
      ctx.stroke();
      ctx.restore();
    }

    // ---------- pointer ----------
    point(e) {
      const rect = this.el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    moveCleaner(x, y) {
      this.cleaner.style.transform = `translate(${x}px, ${y}px)`;
    }

    park() {
      if (this.done || this.auto) return;
      this.el.classList.add('is-idle');
    }

    start() {
      if (!this.el.classList.contains('has-started')) this.el.classList.add('has-started');
      this.el.classList.remove('is-idle');
    }

    onDown(e) {
      if (this.done || this.auto) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.pressing = true;
      this.start();
      this.el.classList.add('is-pressing');
      const p = this.point(e);
      this.last = p;
      this.moveCleaner(p.x, p.y);
      this.wipe(p.x, p.y, p.x, p.y);
      if (e.pointerType === 'mouse') e.preventDefault();
    }

    onMove(e) {
      if (this.done || this.auto) return;
      const p = this.point(e);
      if (e.pointerType === 'mouse' || this.pressing) {
        this.el.classList.remove('is-idle');
        this.moveCleaner(p.x, p.y);
      }
      if (!this.pressing) return;
      this.wipe(this.last.x, this.last.y, p.x, p.y);
      this.last = p;
      this.maybeCheck();
    }

    onUp() {
      if (!this.pressing) return;
      this.pressing = false;
      this.el.classList.remove('is-pressing');
      this.check();
    }

    // ---------- progress ----------
    maybeCheck() {
      const now = performance.now();
      if (now - this.lastCheck > 220) {
        this.lastCheck = now;
        this.check();
      }
    }

    cleanShare() {
      const { width, height } = this.canvas;
      const data = this.ctx.getImageData(0, 0, width, height).data;
      const step = 24; // sample every 24th pixel
      let clear = 0;
      let total = 0;
      for (let i = 3; i < data.length; i += 4 * step) {
        total++;
        if (data[i] < 60) clear++;
      }
      return total ? clear / total : 0;
    }

    check() {
      if (!this.done && this.cleanShare() >= doneAt()) this.finish();
    }

    // ---------- auto clean ----------
    autoClean() {
      if (this.done || this.auto) return;
      this.auto = true;
      this.start();
      if (reducedMotion.matches) {
        this.finish();
        return;
      }
      const rows = Math.max(3, Math.ceil(this.h / (this.brush * 0.85)));
      const pad = this.brush * 0.35;
      const pts = [];
      for (let r = 0; r < rows; r++) {
        const y = pad + ((this.h - pad * 2) * r) / (rows - 1);
        pts.push(r % 2 ? { x: this.w - pad, y } : { x: pad, y }, r % 2 ? { x: pad, y } : { x: this.w - pad, y });
      }
      const segLen = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
      const lengths = pts.slice(1).map((p, i) => segLen(pts[i], p));
      const total = lengths.reduce((a, b) => a + b, 0);
      const duration = Math.min(2600, 900 + total * 0.35);
      const t0 = performance.now();
      let prev = pts[0];
      this.el.classList.add('is-pressing');

      const tick = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        let dist = eased * total;
        let i = 0;
        while (i < lengths.length - 1 && dist > lengths[i]) dist -= lengths[i++];
        const a = pts[i];
        const b = pts[i + 1];
        const k = lengths[i] ? Math.min(1, dist / lengths[i]) : 1;
        const p = { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
        this.wipe(prev.x, prev.y, p.x, p.y);
        this.moveCleaner(p.x, p.y);
        prev = p;
        if (t < 1) requestAnimationFrame(tick);
        else this.finish();
      };
      requestAnimationFrame(tick);
    }

    // ---------- finish ----------
    finish() {
      if (this.done) return;
      this.done = true;
      this.el.classList.remove('is-pressing', 'is-idle');
      this.el.classList.add('has-started', 'is-done');
      this.section.classList.add('is-inview');
      const remove = () => {
        this.el.hidden = true;
        this.ro.disconnect();
      };
      if (reducedMotion.matches) remove();
      else setTimeout(remove, 800);
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('[data-clr-wipe-section]').forEach((section) => {
      if (section.clrWipe || !section.querySelector('[data-clr-wipe]')) return;
      section.clrWipe = new DirtyWindow(section);
    });
  };

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
